// ⚠ FROM '../constants'. Importing API_BASE_URL from '../config/appConfig' -- which exports no
// such name -- does NOT fail: a missing named export is `undefined`, so every request would go to
// `/undefined/api/...` with lint and the tests green. That shipped once on the analog scanner.
import { API_BASE_URL } from '../constants';

/**
 * The Pivot Planetary Commonalities study's client, and the rules for presenting it.
 *
 * <h2>SYNCHRONOUS, unlike the planetary analog scanner</h2>
 *
 * That one is submit-then-poll because an uncached hundred-year sky grid outlasts the request
 * timeout. This reads the ephemeris twice per PIVOT -- a few thousand calls for a thirty-year
 * chart -- and the pair walk is arithmetic over those snapshots. There is no slow case for a
 * polling protocol to rescue, and a protocol with nothing to do is one more thing to get wrong.
 *
 * <h2>⚠ BITS MEASURE COINCIDENCE, NOT CONFIDENCE</h2>
 *
 * A key's bits are `-log2(its measured share of the pairs that were ASKED)`. Ten bits means
 * roughly one pair in a thousand showed it. That is a statement about how unusual an agreement
 * is within THIS run's own history -- not a probability that anything follows a pivot, and not a
 * claim that the agreement caused the pivot. The study ships no forward-return column at all,
 * deliberately: every other engine in this repository that measured one on a geometric signal
 * came back with no edge, and a column labelled "confidence" would invite the opposite reading.
 */

/** The defaults the server serves; mirrored only so the dialog can render before `/options`. */
export const DEFAULTS = {
  window: 20,
  minSeparation: 20,
  orbDegrees: 1.0,
  d9MinSpan: 40,
  pairLimit: 60,
};

/**
 * The six commonality kinds, with the short code the matrix and the chart marks share.
 *
 * <p>⚠ MIRRORED FOR LABELLING ONLY. The server's `kind` string is the source of truth and an
 * unknown one falls back to its own raw name rather than being dropped -- a kind added in Java
 * and missing here is a backend value the frontend has not caught up with, and showing it is how
 * that gets noticed where a blank would hide it.
 */
export const KINDS = {
  RETURN: { code: 'RET', label: 'Same-planet longitude return', group: 'Longitude' },
  TRANSFER: { code: 'XFR', label: 'Cross-planet longitude transfer', group: 'Longitude' },
  D9_SIGN: { code: 'D9S', label: 'Navamsha sign shared', group: 'Navamsha' },
  D9_PADA: { code: 'D9P', label: 'Navamsha same pada', group: 'Navamsha' },
  D9_SWAP: { code: 'D9X', label: 'Navamsha reciprocal exchange', group: 'Navamsha' },
  D9_ASPECT: { code: 'D9A', label: 'Navamsha mutual aspect', group: 'Navamsha' },
};

/** Which kinds are only asked of a pair whose span clears `d9MinSpan`. */
export const D9_KINDS = ['D9_SIGN', 'D9_PADA', 'D9_SWAP', 'D9_ASPECT'];

export const isD9Kind = (kind) => D9_KINDS.includes(kind);

export const kindCode = (kind) => (KINDS[kind] ? KINDS[kind].code : String(kind || '?'));

export const kindLabel = (kind) => (KINDS[kind] ? KINDS[kind].label : String(kind || 'Unknown'));

/** The four pivot transitions, in the order the brief names them. */
export const TRANSITIONS = {
  HIGH_HIGH: 'High → High',
  HIGH_LOW: 'High → Low',
  LOW_HIGH: 'Low → High',
  LOW_LOW: 'Low → Low',
};

const authHeaders = (token) => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

/** The SERVER'S own message where there is one -- a 403 here means the admin gate refused. */
async function readError(response) {
  let detail = `HTTP ${response.status}`;
  try {
    const body = await response.json();
    if (body && body.error) detail = body.error;
  } catch (ignored) { /* a non-JSON error body is not worth a second failure */ }
  return new Error(detail);
}

/**
 * What the panel should open on.
 *
 * <p>SERVED rather than hardcoded, for the reason the analog scanner's factor list is: a default
 * changed in Java otherwise leaves the dialog showing a number the run does not use.
 */
export async function fetchOptions(token) {
  const r = await fetch(`${API_BASE_URL}/api/pivot-commonalities/options`,
    { headers: authHeaders(token) });
  if (!r.ok) throw await readError(r);
  return r.json();
}

export async function runScan(token, body, { signal } = {}) {
  const r = await fetch(`${API_BASE_URL}/api/pivot-commonalities/scan`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
    signal,
  });
  if (!r.ok) throw await readError(r);
  return r.json();
}

// ------------------------------------------------------------------ presentation

/** Degrees as `12°34'`, which is the unit the ±1° orb is actually read in. */
export function formatDelta(deg) {
  if (!Number.isFinite(deg)) return '—';
  const whole = Math.floor(deg);
  const minutes = Math.round((deg - whole) * 60);
  // A rounding that reaches 60 must carry, or a 0.9999° delta prints as 0°60'.
  if (minutes === 60) return `${whole + 1}°00'`;
  return `${whole}°${String(minutes).padStart(2, '0')}'`;
}

/** A pair's two dates, which is what the chart is asked to mark. */
export const pairKey = (pair) => (pair && pair.a && pair.b ? `${pair.a.date}→${pair.b.date}` : '');

/**
 * A one-line summary of what the scan actually did.
 *
 * <p>Every count a reader needs to tell "the study found nothing" from "the study was never
 * asked" is in here. `pivotsDropped` and `tooCloseDropped` especially: a run that silently
 * discarded half its pivots reads identically to a quiet market.
 */
export function describeScan(report) {
  if (!report) return '';
  const parts = [
    `${report.pivotCount} pivots`,
    `${report.from} → ${report.to}`,
    `${report.bars} bars`,
    `${report.pairsEvaluated} pairs`,
    `${report.totalMatches} matches`,
  ];
  if (report.d9PairsEvaluated !== report.pairsEvaluated) {
    parts.push(`${report.d9PairsEvaluated} pairs reached the D9 span`);
  }
  if (report.tooCloseDropped) parts.push(`${report.tooCloseDropped} dropped as too close`);
  if (report.ambiguousBars) parts.push(`${report.ambiguousBars} ambiguous bars`);
  if (report.pivotsDropped) parts.push(`${report.pivotsDropped} oldest pivots dropped`);
  if (typeof report.elapsedMillis === 'number') {
    parts.push(`${(report.elapsedMillis / 1000).toFixed(1)}s`);
  }
  return parts.join(' · ');
}

/**
 * The pairs in which one commonality key appears, with the pair's own index.
 *
 * <p>⚠ ONLY THE PAIRS THAT CAME BACK, AND ONLY THEIR KEPT MATCHES -- which are TWO caps, not
 * one, and the second is the one that makes most of the matrix unclickable.
 *
 * <pre>
 *   pairLimit (60)              how many pairs come back at all
 *   matchesPerPair (14)         how many of a pair's matches come back, STRONGEST FIRST
 * </pre>
 *
 * Measured on SPY: 361 matrix rows, **188 reachable and 173 dead**, with the first reachable
 * row at index 118. Every one of the 110 `D9_ASPECT` rows is dead -- a pair averages 56
 * matches and keeps 14, and D9A is the weakest kind, so it is truncated out of every one.
 *
 * <p>Both caps are right where they are. The per-pair one orders by bits because that is what
 * the hover card should lead with; raising it would fill every card with 1.2-bit noise. What
 * was wrong is the UI pretending a dead row was selectable and then blaming the pair limit.
 */
export function pairsCarrying(report, key) {
  const pairs = (report && report.pairs) || [];
  const out = [];
  pairs.forEach((pair, index) => {
    const hit = (pair.matches || []).find((m) => m.key === key);
    if (hit) out.push({ pair, index, match: hit });
  });
  return out;
}

/**
 * The matrix keys that ANY returned pair can actually show.
 *
 * <p>Computed once per report and handed to the table, because asking it per row is a scan of
 * sixty pairs times three hundred rows on every render.
 */
export function reachableKeys(report) {
  const keys = new Set();
  ((report && report.pairs) || []).forEach((pair) => {
    (pair.matches || []).forEach((m) => keys.add(m.key));
  });
  return keys;
}

/**
 * The strongest pair carrying a key, with the match itself.
 *
 * <p>The pairs arrive bits-descending, so the FIRST one carrying the key is the strongest --
 * no re-sort, and the chart's choice agrees with the Pairs tab's own ordering by construction.
 * Null when the key is unreachable, which the caller must say rather than paint nothing.
 */
export function strongestPairFor(report, key) {
  return pairsCarrying(report, key)[0] || null;
}

/**
 * ⚠ THE BODIES THAT PARTICIPATE, counted over the MATRIX rather than over the pair list.
 *
 * The pair list is capped and the matrix is not, so counting the pairs would make the most
 * frequent body a function of `pairLimit`. A transfer names two bodies and both are credited;
 * a return names one, twice over, and must be credited ONCE -- otherwise every return outscores
 * every transfer by construction and the ranking says nothing but "returns are commoner".
 */
export function bodyParticipation(report) {
  const rows = (report && report.commonalities) || [];
  const tally = new Map();
  rows.forEach((c) => {
    const bodies = c.from === c.to ? [c.from] : [c.from, c.to];
    bodies.forEach((b) => {
      const prior = tally.get(b) || { body: b, count: 0, keys: 0, bits: 0 };
      prior.count += c.count;
      prior.keys += 1;
      prior.bits += c.bits * c.count;
      tally.set(b, prior);
    });
  });
  return [...tally.values()].sort((x, y) => y.count - x.count);
}

/**
 * ⚠ THE MATRIX IS DOMINATED BY ONE KIND, AND NO ORDERING FIXES IT — the FILTER does.
 *
 * <p>Measured on SPY, 1993–2026: 183 pivots, 16,653 pairs, 937,392 matches, 361 matrix rows.
 *
 * <pre>
 *   kind  keys   total count   bits        what it is
 *   D9A    110       759,027   1.2–1.3     a D9 mutual aspect: 4 of 7 folded sign distances
 *   D9X    110       151,447   3.4–3.9     a D9 reciprocal exchange
 *   D9S     11        12,196   3.7–4.6
 *   D9P     11         2,718   4.7–7.4
 *   XFR    108        10,303   5.1–10.4    the brief's cross-planet transfer
 *   RET     11         1,701   5.4–7.8     the brief's same-planet return
 * </pre>
 *
 * <p>A D9 mutual aspect fires on about 43% of every pair it is asked of, because four of the
 * seven folded sign distances qualify. So 220 of the 361 rows are the two weakest kinds, and
 * the two the brief leads with sit below all of them.
 *
 * <h2>A `count x bits` SORT WAS BUILT FOR THIS AND IS NOT SHIPPED</h2>
 *
 * It looked like the answer and the arithmetic says it is not: the counts span four orders of
 * magnitude and the bits span one, so `count x bits` reproduces count-order almost exactly —
 * at each kind's head it gives D9A 8,738 · D9X 5,450 · D9S 4,788 · D9P 2,956 · XFR 2,433 ·
 * RET 2,111, which is the same sequence. A third chip that reorders nothing would be a control
 * that lies, and that is worse than two that do not.
 *
 * <p>So the ORDER stays the server's own — which is the brief's question, and ranked by
 * improbability instead every one-off would be at the top — and the instrument that actually
 * works is `kinds`: untick D9A and D9X and the table becomes the two kinds the brief is about.
 *
 * <pre>
 *   count   the brief's own question: what RECURS. The server's order.
 *   bits    what is RAREST — a one-off floats to the top, which is why it is not the default
 * </pre>
 */
export const SORTS = {
  count: { label: 'Frequency', of: (c) => c.count },
  bits: { label: 'Rarity (bits)', of: (c) => c.bits },
};

export const DEFAULT_SORT = 'count';

/**
 * Sort and filter the matrix.
 *
 * <p>A COPY, never in place: the report object is held in state and sorting it where it lies
 * would mutate what a second reader of the same object sees.
 *
 * <p>The tie-break is COUNT then KEY, so the order is total — two keys with identical scores
 * must not reorder between renders, which reads as the table flickering.
 */
export function sortCommonalities(rows, sort = DEFAULT_SORT, kinds = null) {
  const of = (SORTS[sort] || SORTS[DEFAULT_SORT]).of;
  // ⚠ NULL MEANS EVERY KIND; AN EMPTY ARRAY MEANS NONE. Collapsing the two -- which is the
  // obvious `kinds && kinds.length` -- makes "I unticked them all" show the whole table, so the
  // filter silently contradicts the chips a reader just turned off.
  const filtered = Array.isArray(kinds)
    ? (rows || []).filter((c) => kinds.includes(c.kind))
    : (rows || []);
  return [...filtered].sort((a, b) => (of(b) - of(a)) || (b.count - a.count)
    || String(a.key).localeCompare(String(b.key)));
}

/** Which kinds this report actually produced, with their row counts, for the filter chips. */
export function kindTally(report) {
  const tally = new Map();
  ((report && report.commonalities) || []).forEach((c) => {
    const prior = tally.get(c.kind) || { kind: c.kind, keys: 0, count: 0 };
    prior.keys += 1;
    prior.count += c.count;
    tally.set(c.kind, prior);
  });
  // In the DECLARED order rather than by size, so the chips do not reshuffle between scans.
  return Object.keys(KINDS).filter((k) => tally.has(k)).map((k) => tally.get(k));
}

/**
 * The pivot rows, with the separation a reader checks the constraint against.
 *
 * <p>The server already stamps `barsSincePrevious`; this only guarantees the field exists, so a
 * cached response from before it was added renders a dash rather than `undefined`.
 */
export function pivotRows(report) {
  return ((report && report.pivots) || []).map((p, i) => ({
    ...p,
    index: i,
    barsSincePrevious: Number.isFinite(p.barsSincePrevious) ? p.barsSincePrevious : null,
  }));
}
